import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Button,
  Input,
  Label,
  Radio,
  RadioGroup,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import type { QuoteUpdateDto } from "library/types/types";

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: "600px",
    width: "100%",
  },
  dialogBody: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "12px",
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  fieldRow: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  radioGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  infoText: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    marginTop: "4px",
  },
  originalValue: {
    fontSize: "12px",
    color: tokens.colorNeutralForeground3,
    fontWeight: 600,
    marginTop: "4px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  quoteInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "12px",
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: "8px",
  },
  quoteInfoRow: {
    display: "flex",
    gap: "8px",
    fontSize: "13px",
  },
  quoteInfoLabel: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    minWidth: "140px",
  },
  quoteInfoValue: {
    color: tokens.colorNeutralForeground1,
  },
});

type EditMode = "override" | "remove" | "setNull";

export interface EditPremiumDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (update: QuoteUpdateDto) => void;
  quoteData: {
    insurer: string;
    period: number;
    payments: number;
    variantID: number | null;
    originalPremium: number | null;
    currentPremium: number | null;
  };
  insuranceType: "mtpl" | "casco";
}

const EditPremiumDialog = ({
  open,
  onClose,
  onSave,
  quoteData,
  insuranceType,
}: EditPremiumDialogProps) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const [editMode, setEditMode] = useState<EditMode>("override");
  const [premium, setPremium] = useState<string>("");

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      console.log("EditPremiumDialog opened with quoteData:", quoteData);
      console.log(
        "  currentPremium:",
        quoteData.currentPremium,
        "originalPremium:",
        quoteData.originalPremium
      );
      console.log(
        "  Condition check: currentPremium !== originalPremium:",
        quoteData.currentPremium !== quoteData.originalPremium
      );

      const currentValue =
        quoteData.currentPremium ?? quoteData.originalPremium;
      if (currentValue !== null) {
        setPremium(currentValue.toString());
      } else {
        setPremium("");
      }

      // Determine initial edit mode
      if (
        quoteData.currentPremium === null &&
        quoteData.originalPremium !== null
      ) {
        // When current is null, default to "remove" to restore original
        console.log("  Setting editMode to 'remove' (current is null)");
        setEditMode("remove");
      } else if (
        quoteData.currentPremium !== null &&
        quoteData.originalPremium !== null &&
        quoteData.currentPremium !== quoteData.originalPremium
      ) {
        console.log("  Setting editMode to 'override' (has override)");
        setEditMode("override");
      } else {
        console.log("  Setting editMode to 'override' (default)");
        setEditMode("override");
      }
    }
  }, [open, quoteData]);

  const handleSave = () => {
    let finalPremium: number | null = null;

    if (editMode === "override") {
      const parsedPremium = parseFloat(premium);
      if (!isNaN(parsedPremium) && parsedPremium >= 0) {
        finalPremium = parsedPremium;
      } else {
        // Invalid input, use original
        finalPremium = quoteData.originalPremium;
      }
    } else if (editMode === "remove") {
      // Return to original value
      finalPremium = quoteData.originalPremium;
    } else if (editMode === "setNull") {
      // Set to null (not offered)
      finalPremium = null;
    }

    const update: QuoteUpdateDto = {
      Insurer: quoteData.insurer,
      Premium: finalPremium,
      Period: quoteData.period,
      Payments: quoteData.payments,
      VariantID: quoteData.variantID,
      // Don't include deductible fields - they're not being edited in this dialog
      // This prevents them from appearing as "updated to null" in the display
    };

    onSave(update);
    onClose();
  };

  const isCasco = insuranceType === "casco";

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody className={styles.dialogBody}>
          <div className={styles.header}>
            <DialogTitle>
              {t("qtg.editPremium.title", "Edit Quote Values")}
            </DialogTitle>
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              onClick={onClose}
            />
          </div>

          <DialogContent>
            {/* Quote Information */}
            <div className={styles.quoteInfo}>
              <div className={styles.quoteInfoRow}>
                <span className={styles.quoteInfoLabel}>
                  {t("qtg.editPremium.insurer", "Insurer")}:
                </span>
                <span className={styles.quoteInfoValue}>
                  {quoteData.insurer}
                </span>
              </div>
              <div className={styles.quoteInfoRow}>
                <span className={styles.quoteInfoLabel}>
                  {t("qtg.editPremium.period", "Period")}:
                </span>
                <span className={styles.quoteInfoValue}>
                  {quoteData.period} {t("qtg.editPremium.months", "months")}
                </span>
              </div>
              <div className={styles.quoteInfoRow}>
                <span className={styles.quoteInfoLabel}>
                  {t("qtg.editPremium.payments", "Payments")}:
                </span>
                <span className={styles.quoteInfoValue}>
                  {quoteData.payments}
                </span>
              </div>
              {isCasco && quoteData.variantID !== null && (
                <div className={styles.quoteInfoRow}>
                  <span className={styles.quoteInfoLabel}>
                    {t("qtg.editPremium.variant", "Variant")}:
                  </span>
                  <span className={styles.quoteInfoValue}>
                    {quoteData.variantID}
                  </span>
                </div>
              )}
            </div>

            {/* Edit Mode Selection */}
            <div className={styles.section}>
              <Label weight="semibold">
                {t("qtg.editPremium.action", "Action")}
              </Label>
              <RadioGroup
                value={editMode}
                onChange={(_, data) => setEditMode(data.value as EditMode)}
                className={styles.radioGroup}
              >
                <Radio
                  value="override"
                  label={t(
                    "qtg.editPremium.override",
                    "Override with custom value"
                  )}
                />
                {/* Show "Remove override" only if current value differs from original (including when null) */}
                {quoteData.currentPremium !== quoteData.originalPremium && (
                  <Radio
                    value="remove"
                    label={t(
                      "qtg.editPremium.remove",
                      "Remove override (restore original)"
                    )}
                  />
                )}
                {/* Show "Set to null" only if there's a current non-null value */}
                {quoteData.currentPremium !== null && (
                  <Radio
                    value="setNull"
                    label={t(
                      "qtg.editPremium.setNull",
                      "Set to null (not offered)"
                    )}
                  />
                )}
              </RadioGroup>
            </div>

            {/* Premium Input */}
            {editMode === "override" && (
              <div className={styles.section}>
                <div className={styles.fieldRow}>
                  <Label htmlFor="premium" weight="semibold">
                    {t("qtg.editPremium.premium", "Premium")} (€)
                  </Label>
                  <Input
                    id="premium"
                    type="number"
                    step="0.01"
                    min="0"
                    value={premium}
                    onChange={(_, data) => setPremium(data.value)}
                  />
                  {quoteData.originalPremium !== null && (
                    <div className={styles.originalValue}>
                      {t("qtg.editPremium.original", "Original")}: €
                      {quoteData.originalPremium.toFixed(2)}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Info text for remove/setNull modes */}
            {editMode === "remove" && (
              <div className={styles.infoText}>
                {t(
                  "qtg.editPremium.removeInfo",
                  "This will restore the original calculated premium value."
                )}
              </div>
            )}
            {editMode === "setNull" && (
              <div className={styles.infoText}>
                {t(
                  "qtg.editPremium.setNullInfo",
                  "This quote will be marked as not offered and will display as unavailable."
                )}
              </div>
            )}
          </DialogContent>

          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              {t("qtg.editPremium.cancel", "Cancel")}
            </Button>
            <Button appearance="primary" onClick={handleSave}>
              {t("qtg.editPremium.save", "Save Changes")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default EditPremiumDialog;
